package com.dotdrives.controller

import com.dotdrives.core.*
import com.dotdrives.model.*
import com.dotdrives.model.Target
import com.dotdrives.repository.*
import kotlinx.coroutines.*
import kotlinx.datetime.LocalDate

data class ConfigurationSave(
    val requestId: String,
    val processId: String,
    val itemId: String,
    val name: String,
    val isPhase: Boolean,
)

data class ConfigurationState(
    val snapshot: ConfigurationSnapshot? = null,
    val loading: Boolean = false,
    val saving: Boolean = false,
    val error: String? = null,
    val saved: Boolean = false,
    val savedChange: ConfigurationSave? = null,
    val refreshError: String? = null,
)

class ConfigurationController(
    private val repo: ConfigurationRepository,
    scope: CoroutineScope,
    private val org: String,
    private val role: String,
) : DotController<ConfigurationState>(ConfigurationState(), scope) {
    private var generation = 0

    fun load() {
        val gen = ++generation
        update { it.copy(loading = true, refreshError = null, error = if (it.snapshot == null) null else it.error) }
        scope.launch {
            repo
                .load(org)
                .onSuccess { value ->
                    if (gen == generation) update { it.copy(snapshot = value, loading = false) }
                }
                .onFailure { e ->
                    if (e is CancellationException) throw e
                    if (gen == generation)
                        update {
                            it.copy(
                                loading = false,
                                error = if (it.snapshot == null) "Unable to load process and targets. Retry." else it.error,
                                refreshError = if (it.snapshot != null) "Your saved changes are kept. The latest configuration could not be refreshed." else null,
                            )
                        }
                }
        }
    }

    fun saveTarget(
        id: String?,
        name: String,
        amount: String,
        start: String,
        end: String,
        active: Boolean,
    ) {
        val n = amount.takeIf { it.isNotBlank() }?.toDoubleOrNull()
        if (name.isBlank() || (amount.isNotBlank() && (n == null || !n.isFinite() || n < 0))) {
            fail("Enter a target name and a valid nonnegative goal.")
            return
        }
        val datesValid =
            runCatching {
                    if (start.isBlank() && end.isBlank()) true
                    else LocalDate.parse(end) >= LocalDate.parse(start)
                }
                .getOrDefault(false)
        if (!datesValid) {
            fail("Set both goal dates with the end on or after the start, or leave both blank.")
            return
        }
        val value = Target(
            id ?: uuidV7(), org, name.trim(), n, active,
            start.takeIf { it.isNotBlank() }, end.takeIf { it.isNotBlank() },
        )
        save(merge = { snapshot ->
            snapshot.copy(targets = snapshot.targets.filterNot { it.id == value.id } + value)
        }) { repo.target(value) }
    }

    fun savePhase(
        existing: DrivePhase?,
        template: String,
        name: String,
        sequence: String,
        terminal: Boolean,
        requestId: String = uuidV7(),
    ) {
        if (existing != null && (existing.templateId != template || existing.orgId != org)) {
            fail(
                "Existing phases stay in their original process. Add a new phase to use a different process."
            )
            return
        }
        val order = sequence.toIntOrNull()
        if (name.isBlank() || template.isBlank() || order == null || order < 0) {
            fail("Choose a process, phase name, and nonnegative order.")
            return
        }
        val value = DrivePhase(
            id = existing?.id ?: requestId,
            orgId = org,
            templateId = template,
            name = name.trim(),
            description = existing?.description.orEmpty(),
            exampleText = existing?.exampleText.orEmpty(),
            sequence = order,
            isTerminal = terminal,
        )
        save(
            change = ConfigurationSave(requestId, template, value.id, value.name, true),
            merge = { it.copy(phases = it.phases.filterNot { phase -> phase.id == value.id } + value) },
        ) { repo.phase(value) }
    }

    fun saveTemplate(name: String, requestId: String = uuidV7()) {
        if (name.isBlank()) {
            fail("Enter a process name.")
            return
        }
        val value = DrivePhaseTemplate(requestId, org, name.trim(), false, true, true)
        save(
            change = ConfigurationSave(requestId, value.id, value.id, value.name, false),
            merge = { it.copy(templates = (it.templates.filterNot { process -> process.id == value.id } + value).sortedBy { process -> process.name }) },
        ) { repo.template(value) }
    }

    fun clearError() { update { it.copy(error = null) } }

    private fun save(
        change: ConfigurationSave? = null,
        merge: (ConfigurationSnapshot) -> ConfigurationSnapshot = { it },
        block: suspend () -> Result<Unit>,
    ) {
        if (!canAdminister(role)) {
            fail("Only an owner or administrator can change configuration.")
            return
        }
        if (state.value.saving) return
        // A repeated acknowledgement/submission must not create the same editor's item twice.
        if (change != null && state.value.savedChange?.requestId == change.requestId) return
        update { it.copy(saving = true, error = null, saved = false) }
        scope.launch {
            block()
                .onSuccess {
                    update {
                        it.copy(
                            saving = false,
                            saved = true,
                            savedChange = change,
                            snapshot = merge(it.snapshot ?: ConfigurationSnapshot(emptyList(), emptyList(), emptyList())),
                        )
                    }
                    load()
                }
                .onFailure { e ->
                    if (e is CancellationException) throw e
                    update {
                        it.copy(
                            saving = false,
                            error =
                                "Configuration could not be saved. Your changes are still here. Check your connection and try again.",
                        )
                    }
                }
        }
    }

    private fun fail(message: String) {
        update { it.copy(error = message) }
    }
}
